import { useEffect } from "react";

/**
 * Mounts a single global IntersectionObserver that toggles `.in-view` on any
 * element carrying the `.reveal` class, plus `[data-reveal]` sections. Also
 * auto-reveals common content blocks (h1/h2, sections, articles, cards) so the
 * app gains microinteractions site-wide with zero per-page wiring.
 */
export default function AutoReveal() {
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );

    const observed = new WeakSet<Element>();

    const attach = () => {
      const nodes = document.querySelectorAll<HTMLElement>(".reveal, [data-reveal]");
      nodes.forEach((n) => {
        if (observed.has(n)) return;
        observed.add(n);
        io.observe(n);
      });
    };

    attach();

    const mo = new MutationObserver(() => attach());
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      io.disconnect();
    };
  }, []);

  return null;
}
